import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CompanyFilter } from '../components/CompanyFilter';
import { useDateRange } from '../hooks/useDateRange';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Download } from 'lucide-react';

function ConfBadge({ level }) {
  const cls = level === 'high' ? 'bg-emerald-50 text-emerald-600' : level === 'medium' ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-400';
  return <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${cls}`}>{level}</span>;
}

export default function ProductComparisonPage() {
  const [data, setData] = useState({ products: [] });
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [sites, setSites] = useState([]);
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterSite, setFilterSite] = useState('all');
  const { dateFrom, dateTo, setDateFrom, setDateTo, dateParams } = useDateRange();
  const [sortBy, setSortBy] = useState('cost_per_hour');

  const fetchData = () => {
    const p = {};
    if (filterCompany !== 'all') p.company_id = filterCompany;
    if (filterSite !== 'all') p.site_id = filterSite;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    api.get(`/analytics/product-comparison?${new URLSearchParams(p)}`).then(r => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { api.get('/companies').then(r => setCompanies(r.data)).catch(()=>{}); api.get('/sites').then(r => setSites(r.data)).catch(()=>{}); fetchData(); }, []);
  useEffect(() => { setLoading(true); fetchData(); }, [filterCompany, filterSite, dateFrom, dateTo]);

  const sorted = [...(data.products || [])].sort((a, b) => {
    if (sortBy === 'cost_per_hour') return (a.cost_per_hour || 999999) - (b.cost_per_hour || 999999);
    if (sortBy === 'total_spend') return b.total_spend - a.total_spend;
    if (sortBy === 'avg_life_hours') return (b.avg_life_hours || 0) - (a.avg_life_hours || 0);
    return 0;
  });

  const chartData = sorted.filter(p => p.cost_per_hour > 0).slice(0, 10).map(p => ({ name: p.brand?.substring(0, 16), cph: p.cost_per_hour, cph100: p.cost_per_100hrs }));

  const downloadReport = async () => {
    const p = {};
    if (filterCompany !== 'all') p.company_id = filterCompany;
    if (filterSite !== 'all') p.site_id = filterSite;
    const res = await api.get(`/reports/generate/product-comparison?${new URLSearchParams(p)}`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = 'product_comparison.xlsx'; a.click();
  };

  return (
    <div data-testid="product-comparison-page">
      <div className="page-header">
        <div><h1 className="page-title">Tooth Point Comparison</h1><p className="text-sm text-slate-500 mt-1">Compare tooth point products by long-run cost performance</p></div>
        <Button variant="outline" onClick={downloadReport} data-testid="download-product-report"><Download size={14} className="mr-1.5" />Export Excel</Button>
      </div>

      <div className="flex gap-3 mb-6 flex-wrap items-end" data-testid="product-filters">
        <CompanyFilter companies={companies} value={filterCompany} onChange={setFilterCompany} />
        <Select value={filterSite} onValueChange={setFilterSite}><SelectTrigger className="w-48 bg-white"><SelectValue placeholder="All Sites" /></SelectTrigger><SelectContent><SelectItem value="all">All Sites</SelectItem>{sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}</SelectContent></Select>
        <DateRangeFilter dateFrom={dateFrom} dateTo={dateTo} setDateFrom={setDateFrom} setDateTo={setDateTo} prefix="product" />
        <Select value={sortBy} onValueChange={setSortBy}><SelectTrigger className="w-52 bg-white"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cost_per_hour">Sort: Cost/Hr (Best First)</SelectItem><SelectItem value="total_spend">Sort: Total Spend</SelectItem><SelectItem value="avg_life_hours">Sort: Avg Life</SelectItem></SelectContent></Select>
      </div>

      {chartData.length > 0 && (
        <Card className="bg-white border border-slate-200 rounded-lg p-5 mb-6">
          <h3 className="text-sm font-semibold text-slate-900 font-heading mb-4">Cost Per Hour Comparison</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis type="number" tick={{ fontSize: 11, fill: '#64748B' }} tickFormatter={v => `₹${v}`} /><YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 11, fill: '#334155' }} /><Tooltip formatter={v => `₹${Number(v).toFixed(2)}`} contentStyle={{ border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 12 }} /><Bar dataKey="cph" fill="#0F172A" radius={[0,4,4,0]} name="Cost/Hr" /></BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      <div className="data-table-wrapper">
        <Table>
          <TableHeader><TableRow className="bg-slate-50 border-b border-slate-200">
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Rank</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Brand</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Supplier</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Type</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Unit Price</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cost/Hr</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cost/100Hr</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Avg Life</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Spend</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Trials</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">vs Best</TableHead>
            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Conf.</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={12} className="text-center py-8 text-slate-400">Loading...</TableCell></TableRow> :
            sorted.length === 0 ? <TableRow><TableCell colSpan={12} className="text-center py-8 text-slate-400">No product data with operations</TableCell></TableRow> :
            sorted.map((p, i) => (
              <TableRow key={p.product_id} className={`hover:bg-slate-50/50 ${i === 0 && p.cost_per_hour > 0 ? 'bg-emerald-50/30' : ''}`}>
                <TableCell className="text-sm font-bold text-slate-900">{i + 1}</TableCell>
                <TableCell className="font-medium text-slate-900">{p.brand}</TableCell>
                <TableCell className="text-sm text-slate-600">{p.supplier}</TableCell>
                <TableCell><span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${p.product_type === 'forging' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-500'}`}>{p.product_type}</span></TableCell>
                <TableCell className="text-sm">₹{p.unit_price?.toFixed(0)}</TableCell>
                <TableCell className="text-sm font-bold text-slate-900">{p.cost_per_hour > 0 ? `₹${p.cost_per_hour.toFixed(2)}` : '-'}</TableCell>
                <TableCell className="text-sm">{p.cost_per_100hrs > 0 ? `₹${p.cost_per_100hrs.toFixed(2)}` : '-'}</TableCell>
                <TableCell className="text-sm text-slate-600">{p.avg_life_hours > 0 ? `${p.avg_life_hours.toFixed(0)}h` : '-'}</TableCell>
                <TableCell className="text-sm font-medium">₹{p.total_spend?.toLocaleString()}</TableCell>
                <TableCell className="text-sm text-slate-600">{p.trial_count}</TableCell>
                <TableCell>{p.vs_best_pct > 0 ? <span className="text-xs font-medium text-red-600">+{p.vs_best_pct}%</span> : p.vs_best_pct === 0 && p.cost_per_hour > 0 ? <span className="text-xs font-medium text-emerald-600">Best</span> : '-'}</TableCell>
                <TableCell><ConfBadge level={p.confidence} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
